import { Link, useParams } from 'react-router-dom'
import { PortableText } from '@portabletext/react'
import JournalTextArticlePage from './JournalTextArticlePage'
import { useSanityQuery, sanityImageUrl } from './lib/sanity'

const TITLE_FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif'

const ARTICLE_QUERY = `*[_type == "journalArticle" && slug.current == $slug][0]{ title, byline, dateLine, body }`

// Maps Sanity rich text onto the same markup the hardcoded essays use.
const bodyComponents = {
  block: {
    normal: ({ children }) => <p>{children}</p>,
    subheading: ({ children }) => (
      <p>
        <strong>{children}</strong>
      </p>
    ),
  },
  marks: {
    link: ({ value, children }) => (
      <a href={value?.href} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
        {children}
      </a>
    ),
  },
  types: {
    image: ({ value }) => {
      const src = sanityImageUrl(value, 840)
      if (!src) return null
      return (
        <div style={{ textAlign: 'center', margin: '24px 0' }}>
          <img
            src={src}
            alt={value.alt || ''}
            style={{ maxWidth: '100%', width: 'min(420px, 100%)', height: 'auto', display: 'inline-block' }}
          />
        </div>
      )
    },
  },
}

function BackToJournal() {
  return (
    <p className="journalTextArticleMeta">
      <Link to="/digital-journal" style={{ color: 'inherit' }}>
        Back to Digital Journal
      </Link>
    </p>
  )
}

/** Digital Journal article managed in Sanity Studio, at /digital-journal/:slug. */
export default function JournalSanityArticle() {
  const { slug } = useParams()
  const { data: article, status } = useSanityQuery(ARTICLE_QUERY, { slug })

  if (status === 'loading') {
    return (
      <JournalTextArticlePage title="" titleFont={TITLE_FONT}>
        <p className="journalTextArticleMeta">Loading…</p>
      </JournalTextArticlePage>
    )
  }

  if (status === 'error') {
    return (
      <JournalTextArticlePage title="Couldn't load this article" titleFont={TITLE_FONT}>
        <p className="journalTextArticleMeta">Please refresh the page or try again later.</p>
        <BackToJournal />
      </JournalTextArticlePage>
    )
  }

  if (!article) {
    return (
      <JournalTextArticlePage title="Article not found" titleFont={TITLE_FONT}>
        <BackToJournal />
      </JournalTextArticlePage>
    )
  }

  return (
    <JournalTextArticlePage title={article.title} titleFont={TITLE_FONT}>
      {article.byline && <p className="journalTextArticleMeta">{article.byline}</p>}
      {article.dateLine && <p className="journalTextArticleMeta">{article.dateLine}</p>}
      {article.body && <PortableText value={article.body} components={bodyComponents} />}
    </JournalTextArticlePage>
  )
}
